import { DashboardTab } from '../components/Dashboard/types/dashboard.types';
import { FIRST_TIER_TAB_IDS, VALID_TAB_IDS } from '../components/Dashboard/tabs/registry';
import { folderUrl, parseFolderRoute } from '../components/file-browser/folderUrl';

/**
 * **This module is the ONLY place in the dashboard subtree that touches
 * `window.history`** (STD-029 B-5). Two exports write, one pushes and one
 * replaces, and the grep that holds the rule is in the spec:
 *
 *     git grep -n "window.history" -- apps/dashboard-frontend/src
 *
 * ⚠ **Do not read `useParams()` / `useLocation()` inside the Dashboard subtree.**
 * React-router matches the URL at boot and is then no longer told about
 * anything: these writes are raw `pushState`/`replaceState`, which react-router
 * does not observe, so its internal location goes stale between navigations and
 * only resyncs on `popstate`. That is acceptable precisely because nothing under
 * `<Dashboard>` reads router state — the folder id comes from
 * `parseFolderRoute(window.location.pathname)` via the store, on mount and on
 * `popstate`. A `useParams()` here would read the PREVIOUS url.
 */

// Flag to prevent popstate handler from firing when we push state ourselves
let _isPushingState = false;

/**
 * Whether the store is currently pushing browser history itself. The popstate
 * handler reads this to ignore the synthetic event our own pushState emits.
 */
export function isPushingState(): boolean {
  return _isPushingState;
}

/**
 * Build URL search string from tab and serial.
 * First-tier tabs (overview, files, inventory, calendar) never include serial.
 * Overview with no serial returns '' (bare path).
 */
export function buildSearchParams(tab: DashboardTab, serial: string): string {
  const params = new URLSearchParams();
  if (tab !== 'overview') {
    params.set('tab', tab);
  }
  if (serial && !FIRST_TIER_TAB_IDS.has(tab)) {
    params.set('serial', serial);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * The complete url for a dashboard state — pathname AND search.
 *
 * The Files tab is the one tab addressed by PATH: `/files` for the caller's own
 * root, `/files/folder/<object_id>` for every other node, shared folders
 * included. It carries no `?tab=` at all, because the path already says which
 * tab this is and two spellings of one fact drift.
 *
 * A folder id is **never** written off the Files tab. It stays in the store, so
 * returning to the tab restores the folder, but it is not part of any other
 * tab's address.
 */
export function buildDashboardUrl(
  tab: DashboardTab,
  serial: string,
  folderId: string | null,
  /** Only for the slug. Ignored when absent — the id is the address. */
  folderName?: string
): string {
  if (tab === 'files') return folderUrl(folderId, folderName);
  return `/${buildSearchParams(tab, serial)}`;
}

/**
 * Read a url back into dashboard state. The inverse of `buildDashboardUrl`, and
 * the ONE parser — `initializeFromUrl` and `handlePopState` used to carry a copy
 * each, which is how they came to disagree about which of `?tab=` and `?serial=`
 * wins.
 *
 * **The path wins over `?tab=`.** `/files/folder/<id>?tab=maintenance` is the
 * Files tab looking at that folder; the contradictory query param is dropped by
 * the canonicalizing `replaceState` on load. A pathname of `/` means the query
 * decides, exactly as it always has, aliases included.
 *
 * `folderId`/`folderInvalid` are meaningful only when `tab === 'files'`. On any
 * other tab the caller must leave the stored folder alone rather than clearing
 * it — that is what makes leaving and re-entering the tab return you to where
 * you were.
 */
export function parseDashboardUrl(
  pathname: string,
  search: string
): { tab: DashboardTab; serial: string; folderId: string | null; folderInvalid: boolean } {
  const params = new URLSearchParams(search);
  const serial = params.get('serial') || '';
  const route = parseFolderRoute(pathname);

  if (route.kind !== 'none') {
    return {
      tab: 'files',
      serial,
      folderId: route.kind === 'folder' ? route.objectId : null,
      folderInvalid: route.kind === 'invalid',
    };
  }

  const tabParam = params.get('tab');
  let tab: DashboardTab = 'overview';
  if (tabParam) {
    const aliasMap: Record<string, DashboardTab> = {
      statistics: 'profile',
      timeline: 'maintenance',
    };
    const mapped = aliasMap[tabParam] || tabParam;
    tab = VALID_TAB_IDS.has(mapped as DashboardTab) ? (mapped as DashboardTab) : 'overview';
  } else if (serial) {
    // Serial in URL with no explicit tab implies profile
    tab = 'profile';
  }

  return { tab, serial, folderId: null, folderInvalid: false };
}

function writeHistory(
  method: 'push' | 'replace',
  tab: DashboardTab,
  serial: string,
  folderId: string | null,
  folderName?: string
): void {
  const url = buildDashboardUrl(tab, serial, folderId, folderName);
  const state = { tab, serial, folderId };
  if (method === 'replace') {
    window.history.replaceState(state, '', url);
    return;
  }
  _isPushingState = true;
  try {
    window.history.pushState(state, '', url);
  } finally {
    _isPushingState = false;
  }
}

/**
 * Push a history entry for the current tab/serial/folder.
 *
 * Returns without writing when the url would not change, so clicking the folder
 * you are already in adds no entry. The comparison is made on the ROUTE rather
 * than the raw string for the Files tab, because the address bar may legitimately
 * carry a display slug the builder does not reproduce.
 */
export function syncUrlState(
  tab: DashboardTab,
  serial: string,
  folderId: string | null,
  folderName?: string
): void {
  if (urlAlreadyShows(window.location.pathname, window.location.search, tab, serial, folderId)) {
    return;
  }
  writeHistory('push', tab, serial, folderId, folderName);
}

/**
 * Replace the current history entry. Used for the three writes that must not be
 * places you can go Back to: the load-time canonicalization (`?tab=files` →
 * `/files`, alias mapping), the slug/root beautification once a listing
 * resolves, and leaving a dead deep link.
 */
export function replaceUrlState(
  tab: DashboardTab,
  serial: string,
  folderId: string | null,
  folderName?: string
): void {
  writeHistory('replace', tab, serial, folderId, folderName);
}

/**
 * Does an address bar showing `pathname + search` already show this state?
 *
 * The rule that decides whether a push happens at all, so it is **pure and
 * exported**: the caller passes `window.location`, and the node-environment
 * vitest suite — the only cheap tier this app has — can reach every arm.
 *
 * On the Files tab the comparison is on the ROUTE, not the string, because the
 * address bar legitimately carries a display slug that `buildDashboardUrl` does
 * not reproduce; treating that as a difference would push a duplicate entry on
 * every re-navigation to the folder already open.
 *
 * `/files` matches `folderId === null` and **nothing else** — not the caller's
 * own root row id, even though the canonicalizer spells that node `/files` too.
 * The two are kept distinct here on purpose: `null` is the alias the server
 * resolves from the JWT, and the id is a row. Answering `true` for the id would
 * make the url-level normalization decide navigation state, which is the
 * opposite of "normalize the URL, keep the node". The click-level no-op for that
 * case belongs to `isCurrentNode` (`file-browser/navigationModel.ts`), which can
 * see the listing and therefore knows the two spellings name one folder.
 */
export function urlAlreadyShows(
  pathname: string,
  search: string,
  tab: DashboardTab,
  serial: string,
  folderId: string | null
): boolean {
  if (tab === 'files') {
    const route = parseFolderRoute(pathname);
    return (
      (route.kind === 'folder' && route.objectId === folderId) ||
      (route.kind === 'root' && folderId === null)
    );
  }
  return buildDashboardUrl(tab, serial, folderId) === pathname + search;
}
